import { defineStore } from 'pinia';
import { roundWithPrecision } from '@/utils/formatting';
import { denialsStatsApi } from '@/services/denialsStatsService';

const startDateObj = new Date();
startDateObj.setMonth(startDateObj.getMonth() - 7);
startDateObj.setDate(1); // Set to the 1st day of that month
const defaultStartDate = startDateObj.toLocaleDateString('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
});
const defaultEndDate = new Date().toLocaleDateString('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
});

export const useDenialsStore = defineStore('denials', {
  state: () => ({
    searchLimit: 250,
    searchPage: 1,
    searchDirection: 'asc',
    searchField: 'mrn',
    totalRows: 0,
    denialDetailData: [],

    lastUpdated: '',

    //Filters
    filtersApplied: [],
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    defaultStartDate: defaultStartDate,
    defaultEndDate: defaultEndDate,
    defaultSelectedDenialGroupingList: [
      { id: 1, denial_grouping: 'Controllable' },
    ],

    selectedBillingType: '',
    selectedBillingProviderList: [],
    selectedBillingEntityList: [],
    selectedLocationList: [],
    selectedDepartmentList: [],
    selectedFinancialClassList: [],
    selectedPayerList: [],
    selectedPlanList: [],
    selectedDenialCategoryList: [],
    selectedDenialCodeList: [],
    selectedDenialGroupingList: [{ id: 1, denial_grouping: 'Controllable' }],

    currentMonth: 'October',
    monthlyTotal: {
      month: '',
      value: 0,
      percentageValue: 0,
      priorMonth: '',
      colorTernary: 0,
      arrowTernary: 0,
    },
    previousYearAverage: { value: 0, colorTernary: 0 },
    sixMonthAverage: { value: 0, dateRange: '' },
    monthCount: 0,
    monthlyVS6MonthAverage: {
      value: 0,
      lastMonth: '',
      percentageValue: 0,
      colorTernary: 0,
      arrowTernary: 0,
    },
    financialClassInsights: {
      increases: [
        {
          groupName: '',
          value: 0,
          difference: 0,
          additionalPercentage: {
            value: 0,
            arrowTernary: 0,
            colorTernary: 0,
          },
        },
      ],
      decreases: [
        {
          groupName: '',
          value: 0,
          difference: 0,
          additionalPercentage: {
            value: 0,
            arrowTernary: 0,
            colorTernary: 0,
          },
        },
      ],
    },
    categoryInsights: {
      increases: [
        {
          groupName: '',
          monthValue: 0,
          difference: 0,
          additionalPercentage: {
            value: 0,
            arrowTernary: 0,
            colorTernary: 0,
          },
        },
      ],
      decreases: [
        {
          groupName: '',
          monthValue: 0,
          difference: 0,
          additionalPercentage: {
            value: 0,
            arrowTernary: 0,
            colorTernary: 0,
          },
        },
      ],
    },

    isLoading: false,

    denialGroupingOptions: [
      { id: 1, denial_grouping: 'Controllable' },
      { id: 2, denial_grouping: 'Uncontrollable' },
    ],

    splitBy: { text: 'None', id: 'none' },
    breakoutChart: {},

    //breakout table
    breakoutPageLimit: 250,
    breakoutPageNumber: 1,
    breakoutSearchDirection: 'asc',
    breakoutSearchField: 'some_category',
    breakoutTotalRows: 0,
    breakoutHeaders: [],

    lastFetchFingerprint: null,
  }),
  getters: {
    getFiltersApplied: state => {
      return state.filtersApplied;
    },
  },
  actions: {
    async fetchDenialDetails(page = 1, direction = 'asc', field = 'mrn') {
      //so on pagination results will persist
      this.searchPage = page;
      this.searchDirection = direction;
      this.searchField = field;

      this.isLoading = true;

      const payload = this._buildPayload();
      const pagination = {
        pageNumber: page,
        pageLimit: this.searchLimit,
        sortField: field,
        sortOrder: direction,
      };
      payload.pagination = pagination;
      try {
        const response = await denialsStatsApi.getDetails(payload);
        this.denialDetailData = response.table_data;
        this.totalRows = response.total_rows;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchDenialInsights() {
      this.isLoading = true;
      const payload = this._buildPayload();
      try {
        await Promise.all([
          this._fetchAverageTotalDenials(payload),
          this._fetchFinancialClassInsights(payload),
          this._fetchCategoryInsights(payload),
        ]);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchLastUpdated() {
      const response = await denialsStatsApi.getLastUpdated();
      this.lastUpdated = response.last_updated_timestamp;
    },
    async fetchDenialBreakoutChart(splitBy = this.splitBy) {
      this.splitBy = splitBy;
      const payload = this._buildPayload();
      payload.splitBy = splitBy.id;
      const response = await denialsStatsApi.getBreakoutChart(payload);
      this.breakoutChart.categories = response.categories;
      this.breakoutChart.series = response.series;
    },
    async fetchDenialBreakoutTable(
      splitBy = this.splitBy,
      page = this.breakoutPageNumber,
      limit = this.breakoutPageLimit,
      direction = this.breakoutSearchDirection,
      field = this.breakoutSearchField
    ) {
      if (splitBy.id === 'none') {
        return;
      }

      this.breakoutPageNumber = page;
      this.breakoutPageLimit = limit;
      this.breakoutSearchDirection = direction;
      this.breakoutSearchField = field;
      const payload = this._buildPayload();
      payload.splitBy = splitBy.id;
      let secondSortField = '';

      // Add secondary sort fields based on the split type
      if (
        (splitBy.id === 'payerPlan' || splitBy.id === 'denialCategoryCode') &&
        field === 'some_category'
      ) {
        secondSortField = 'second_category';
      }
      const pagination = {
        pageNumber: page,
        pageLimit: limit,
        sortField: field,
        secondSortField,
        sortOrder: direction,
      };
      payload.pagination = pagination;
      const response = await denialsStatsApi.getBreakoutTable(payload);
      this.breakoutChart.sortableSeries = response.sortableSeries;
      this.breakoutTotalRows = response.totalData;
      this.updateHeaders();
    },
    get_pagination() {
      return {
        sortField: this.searchField,
        sortOrder: this.searchDirection,
      };
    },
    _buildPayload() {
      return {
        filter: {
          startDate: this.startDate,
          endDate: this.endDate,
          billingType:
            this.selectedBillingType == 'Show All'
              ? ''
              : this.selectedBillingType,
          billingProvider: this.selectedBillingProviderList.length
            ? this.selectedBillingProviderList.map(provider => provider.value)
            : [],
          billingEntity: this.selectedBillingEntityList.length
            ? this.selectedBillingEntityList.map(entity => entity.value)
            : [],
          location: this.selectedLocationList.length
            ? this.selectedLocationList.map(location => location.value)
            : [],
          department: this.selectedDepartmentList.length
            ? this.selectedDepartmentList.map(department => department.value)
            : [],
          financialClass: this.selectedFinancialClassList.length
            ? this.selectedFinancialClassList.map(
                financialClass => financialClass.value
              )
            : [],
          payer: this.selectedPayerList.length
            ? this.selectedPayerList.map(payer => payer.value)
            : [],
          plan: this.selectedPlanList.length
            ? this.selectedPlanList.map(plan => plan.plan_name || plan.value)
            : [],
          //send individual denial category and code arrays
          denialCategory: this.selectedDenialCategoryList.length
            ? this.selectedDenialCategoryList.map(item => item.denial_category)
            : [],
          denialCode: this.selectedDenialCodeList.length
            ? this.selectedDenialCodeList.map(
                item => item.claim_adjustment_reason_code
              )
            : [],
          denialGrouping: this.selectedDenialGroupingList.length
            ? this.selectedDenialGroupingList.map(
                denialGrouping => denialGrouping.denial_grouping
              )
            : [],
        },
      };
    },
    async _fetchAverageTotalDenials(payload) {
      const response = await denialsStatsApi.getAverageTotalInsights(payload);
      //Left Card
      this.monthlyTotal.month = response.current_total.previous_month;
      this.monthlyTotal.value = parseFloat(
        response.current_total.previous_month_total
      );
      this.monthlyTotal.percentageValue = roundWithPrecision(
        Math.abs(response.current_total.percent_difference),
        1
      );
      this.monthlyTotal.priorMonth = response.current_total.prior_month;
      this.monthlyTotal.arrowTernary = this._getArrowTernary(
        response.current_total.percent_difference
      );
      this.monthlyTotal.colorTernary = this._getColorTernary(
        response.current_total.percent_difference
      );

      //Middle Card
      this.sixMonthAverage.dateRange = response.six_month_avg.date_range;
      this.sixMonthAverage.value = parseFloat(
        response.six_month_avg.avg_amount
      );

      //Right Card
      this.monthlyVS6MonthAverage.value = parseFloat(
        response.six_month_comparison.difference
      );
      this.monthlyVS6MonthAverage.lastMonth =
        response.six_month_comparison.last_month;
      this.monthlyVS6MonthAverage.percentageValue = roundWithPrecision(
        Math.abs(response.six_month_comparison.percent_difference),
        1
      );
      this.monthlyVS6MonthAverage.arrowTernary = this._getArrowTernary(
        response.six_month_comparison.percent_difference
      );
      this.monthlyVS6MonthAverage.colorTernary = this._getColorTernary(
        response.six_month_comparison.percent_difference
      );
      this.monthCount = response.six_month_comparison.month_count;
    },
    // Helper function for common mapping logic
    _mapInsightItems(items) {
      return items.map(item => ({
        groupName: item.group_name,
        monthValue: parseFloat(item.last_month_avg),
        format: 'currency',
        difference: parseFloat(item.difference),
        additionalPercentage: {
          value: roundWithPrecision(Math.abs(item.percent_difference), 1),
          arrowTernary: this._getArrowTernary(item.percent_difference),
          colorTernary: this._getColorTernary(item.percent_difference),
        },
      }));
    },

    async _fetchFinancialClassInsights(payload) {
      const response = await denialsStatsApi.getFinancialClassInsights(payload);
      this.financialClassInsights.increases = this._mapInsightItems(
        response.financial_class_increase
      );
      this.financialClassInsights.decreases = this._mapInsightItems(
        response.financial_class_decrease
      );
    },

    async _fetchCategoryInsights(payload) {
      const response = await denialsStatsApi.getCategoryInsights(payload);
      this.categoryInsights.increases = this._mapInsightItems(
        response.category_increase
      );
      this.categoryInsights.decreases = this._mapInsightItems(
        response.category_decrease
      );
    },
    resetFilters() {
      this.selectedUser = null;
      this.selectedActivityGap = null;
      this.startDate = this.defaultStartDate;
      this.endDate = this.defaultEndDate;
      this.filtersApplied = [];
    },
    async applyFilters(
      startDate,
      endDate,
      billingType,
      billingProvider,
      billingEntity,
      location,
      department,
      financialClass,
      payerList,
      planList,
      denialCategory,
      denialCode,
      denialGrouping
    ) {

      const incomingFingerprint = JSON.stringify([
        startDate,
        endDate,
        billingType,
        billingProvider,
        billingEntity,
        location,
        department,
        financialClass,
        payerList,
        planList,
        denialCategory,
        denialCode,
        denialGrouping,
      ]);

      if (incomingFingerprint === this.lastFetchFingerprint) {
        return;
      }

      // Make clean copies of everything to break object references
      this.startDate = startDate;
      this.endDate = endDate;
      this.selectedBillingType = billingType;
      this.selectedBillingProviderList = Array.isArray(billingProvider)
        ? [...billingProvider]
        : [];
      this.selectedBillingEntityList = Array.isArray(billingEntity)
        ? [...billingEntity]
        : [];
      this.selectedLocationList = Array.isArray(location) ? [...location] : [];
      this.selectedDepartmentList = Array.isArray(department)
        ? [...department]
        : [];
      this.selectedFinancialClassList = Array.isArray(financialClass)
        ? [...financialClass]
        : [];
      this.selectedPayerList = Array.isArray(payerList) ? [...payerList] : [];
      this.selectedPlanList = Array.isArray(planList) ? [...planList] : [];
      this.selectedDenialCategoryList = Array.isArray(denialCategory)
        ? [...denialCategory]
        : [];
      this.selectedDenialCodeList = Array.isArray(denialCode)
        ? [...denialCode]
        : [];
      this.selectedDenialGroupingList = Array.isArray(denialGrouping)
        ? [...denialGrouping]
        : [];

      // Reset pagination to page 1 when filters are applied
      this.breakoutPageNumber = 1;

      // Calculate applied filters
      this.updateAppliedFilters();

      // Fetch data with new filters
      await Promise.all([
        this.fetchDenialDetails(1, this.searchDirection, this.searchField),
        this.fetchDenialInsights(),
        this.fetchDenialBreakoutChart(this.splitBy),
        this.fetchDenialBreakoutTable(
          this.splitBy,
          this.breakoutPageNumber,
          this.breakoutPageLimit,
          this.breakoutSearchDirection,
          this.breakoutSearchField
        ),
        this.updateHeaders(),
        this.fetchLastUpdated(),
      ]);

      this.lastFetchFingerprint = incomingFingerprint;
    },
    updateAppliedFilters() {
      this.filtersApplied = [];

      if (
        this.startDate !== defaultStartDate ||
        this.endDate !== defaultEndDate
      ) {
        this.filtersApplied.push('Date Range Applied');
      }

      if (this.selectedBillingType && this.selectedBillingType !== 'Show All') {
        this.filtersApplied.push('Billing Type Applied');
      }
      if (this.selectedBillingProviderList?.length) {
        this.filtersApplied.push('Billing Provider Applied');
      }
      if (this.selectedBillingEntityList?.length) {
        this.filtersApplied.push('Billing Entity Applied');
      }
      if (this.selectedLocationList?.length) {
        this.filtersApplied.push('Location Applied');
      }
      if (this.selectedDepartmentList?.length) {
        this.filtersApplied.push('Department Applied');
      }
      if (this.selectedFinancialClassList?.length) {
        this.filtersApplied.push('Financial Class Applied');
      }
      if (this.selectedPayerList?.length) {
        this.filtersApplied.push('Payer Applied');
      }
      if (this.selectedPlanList?.length) {
        this.filtersApplied.push('Plan Applied');
      }
      if (this.selectedDenialCategoryList?.length) {
        this.filtersApplied.push('Denial Category Applied');
      }
      if (this.selectedDenialCodeList?.length) {
        this.filtersApplied.push('Denial Code Applied');
      }
      if (this.selectedDenialGroupingList?.length) {
        this.filtersApplied.push('Denial Grouping Applied');
      }
    },
    async clearFilter(filter) {
      switch (filter) {
        case 'Date Range Applied':
          this.startDate = defaultStartDate;
          this.endDate = defaultEndDate;
          break;
        case 'Billing Type Applied':
          this.selectedBillingType = '';
          break;
        case 'Billing Provider Applied':
          this.selectedBillingProviderList = [];
          break;
        case 'Billing Entity Applied':
          this.selectedBillingEntityList = [];
          break;
        case 'Location Applied':
          this.selectedLocationList = [];
          break;
        case 'Department Applied':
          this.selectedDepartmentList = [];
          break;
        case 'Financial Class Applied':
          this.selectedFinancialClassList = [];
          break;
        case 'Payer Applied':
          this.selectedPayerList = [];
          break;
        case 'Plan Applied':
          this.selectedPlanList = [];
          break;
        case 'Denial Category Applied':
          this.selectedDenialCategoryList = [];
          break;
        case 'Denial Code Applied':
          this.selectedDenialCodeList = [];
          break;
        case 'Denial Grouping Applied':
          this.selectedDenialGroupingList = [];
          break;
        default:
          break;
      }

      await this.applyFilters(
        this.startDate,
        this.endDate,
        this.selectedBillingType,
        this.selectedBillingProviderList,
        this.selectedBillingEntityList,
        this.selectedLocationList,
        this.selectedDepartmentList,
        this.selectedFinancialClassList,
        this.selectedPayerList,
        this.selectedPlanList,
        this.selectedDenialCategoryList,
        this.selectedDenialCodeList,
        this.selectedDenialGroupingList
      );
    },
    _getArrowTernary(percentDifference) {
      return percentDifference > 0 ? 2 : 1;
    },
    _getColorTernary(percentDifference) {
      return percentDifference > 0 ? 1 : 2;
    },
    _mapSelectedListToValues(list, propertyName) {
      return list.length ? list.map(item => item[propertyName]) : [];
    },

    updateHeaders() {
      // Start with the first column (always present)
      this.breakoutHeaders = [
        {
          key: 'some_category',
          // If payerPlan, use "Payer" as the header, otherwise use the splitBy text
          value:
            this.splitBy.id === 'payerPlan'
              ? 'Payer'
              : this.splitBy.id == 'denialCategoryCode'
                ? 'Denial Category'
                : this.splitBy.text,
          sort: true,
        },
      ];

      // Only add second_category column for payerPlan
      if (this.splitBy.id === 'payerPlan') {
        this.breakoutHeaders.push({
          key: 'second_category',
          value: 'Plan',
          sort: true,
        });
      }

      if (this.splitBy.id === 'denialCategoryCode') {
        this.breakoutHeaders.push({
          key: 'second_category',
          value: 'Denial Code',
          sort: true,
        });
      }

      // Add remaining columns (month categories and total)
      this.breakoutHeaders = [
        ...this.breakoutHeaders,
        ...(this.breakoutChart.categories || []).map(category => {
          const fullCategory = category.join(' ');
          return {
            key: fullCategory,
            value: fullCategory,
            format: 'currency',
            sort: true,
          };
        }),
        {
          key: 'total',
          value: 'Total',
          format: 'currency',
          sort: true,
        },
      ];
    },
  },
});
